import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api, { createPublicContact, getMe, uploadContactAttachment } from '@/lib/api';
import { resolveStartPath } from '@/lib/startPage';
import { motion, AnimatePresence } from 'framer-motion';
import { KeyRound, DoorOpen, Eye, EyeOff, MessageSquare, X, UploadCloud, Download, HelpCircle } from 'lucide-react';

// 問い合わせの分類。値は管理人の受信箱にそのまま出るので、既存の英語の値に合わせる
const PASSWORD_RESET_CATEGORY = 'Password Reset';
import { getImageFileFromClipboard } from '@/utils/pasteImage';
import { autoResizeTextarea } from '@/utils/autoResize';

export function Login() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [sessionConflictMessage, setSessionConflictMessage] = useState('');
    const [showSessionModal, setShowSessionModal] = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [restoreMessage, setRestoreMessage] = useState('');
    const [restoreUntil, setRestoreUntil] = useState('');

    // Contact Form State
    const [showContact, setShowContact] = useState(false);
    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactMessage, setContactMessage] = useState('');
    const contactMessageRef = useRef<HTMLTextAreaElement>(null);
    const [contactInquiryNumber, setContactInquiryNumber] = useState('');
    const [contactCategory, setContactCategory] = useState('General');
    const [contactStatus, setContactStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
    const [contactAttachments, setContactAttachments] = useState<string[]>([]);
    const [contactUploading, setContactUploading] = useState(false);
    const [contactAttachmentError, setContactAttachmentError] = useState('');

    useEffect(() => {
        autoResizeTextarea(contactMessageRef.current);
    }, [contactMessage]);

    // 「パスワード再発行」画面などから ?contact=password で来たら、
    // 分類を「パスワードを忘れた」にした問い合わせ窓を開く
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        if (searchParams.get('contact') === 'password') {
            setContactCategory(PASSWORD_RESET_CATEGORY);
            setShowContact(true);
            const next = new URLSearchParams(searchParams);
            next.delete('contact');
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const openPasswordContact = () => {
        setContactCategory(PASSWORD_RESET_CATEGORY);
        setShowContact(true);
    };

    const performLogin = async (force = false, restore = false) => {
        setIsLoading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('username', username);
            formData.append('password', password);

            const res = await api.post('/auth/login', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                params: {
                    ...(force ? { force: '1' } : {}),
                    ...(restore ? { restore: '1' } : {}),
                },
            });

            localStorage.setItem('token', res.data.access_token);
            let nextPath = '/';
            try {
                const meRes = await getMe();
                localStorage.setItem('user', JSON.stringify(meRes.data));
                nextPath = resolveStartPath(meRes.data);
            } catch {
                localStorage.setItem('user', JSON.stringify(res.data));
            }
            setShowSessionModal(false);
            setShowRestoreModal(false);
            // Dramatic delay for effect
            setTimeout(() => navigate(nextPath), 800);
        } catch (err: any) {
            console.error(err);
            const detail = err.response?.data?.detail;
            if (err.response?.status === 409 && detail?.code === 'ACCOUNT_RESTORE_REQUIRED') {
                setRestoreMessage(detail?.message || 'このアカウントは削除待ちです。');
                setRestoreUntil(detail?.restore_until || '');
                setShowRestoreModal(true);
                setIsLoading(false);
                return;
            }
            if (err.response?.status === 409 && (detail?.code === 'SESSION_CONFLICT' || detail === 'SESSION_CONFLICT')) {
                setSessionConflictMessage(detail?.message || 'このアカウントは別の端末で利用中です。続行すると、そちらからは自動的にログアウトします。よろしいですか？');
                setShowSessionModal(true);
                setIsLoading(false);
                return;
            }
            setError(detail?.message || detail || 'ログインに失敗しました。');
            setIsLoading(false);
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        performLogin(false);
    };

    const handleContactSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setContactStatus('sending');
        try {
            await createPublicContact(
                contactName,
                contactEmail,
                contactCategory,
                contactMessage,
                contactInquiryNumber.trim() || undefined,
                contactAttachments
            );
            setContactStatus('success');
            setTimeout(() => {
                setShowContact(false);
                setContactStatus('idle');
                setContactName('');
                setContactEmail('');
                setContactMessage('');
                setContactInquiryNumber('');
                setContactAttachments([]);
                setContactCategory('General');
            }, 2000);
        } catch (err) {
            console.error(err);
            setContactStatus('error');
        }
    };

    const handleContactInquiryNumberChange = (value: string) => {
        const digitsOnly = value.replace(/[^0-9]/g, '').slice(0, 8);
        setContactInquiryNumber(digitsOnly);
    };

    const closeContactModal = () => {
        setShowContact(false);
        setContactAttachmentError('');
        setContactAttachments([]);
        // 「パスワードを忘れた」の導線から開いた後でも、次に普通に開いた時は「一般」に戻す
        setContactCategory('General');
    };

    const isVideoUrl = (url: string) => /\.(mp4|webm|mov|m4v)$/i.test(url.split('?')[0]);

    const handleContactAttachmentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setContactUploading(true);
        setContactAttachmentError('');

        try {
            const uploaded: string[] = [];
            for (const file of files) {
                if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
                    continue;
                }
                const res = await uploadContactAttachment(file);
                if (res.data?.url) {
                    uploaded.push(res.data.url);
                }
            }
            if (uploaded.length) {
                setContactAttachments((prev) => [...prev, ...uploaded]);
            }
        } catch (err) {
            console.error(err);
            setContactAttachmentError('添付のアップロードに失敗しました。');
        } finally {
            setContactUploading(false);
            e.target.value = '';
        }
    };

    const handleContactPasteAttachment = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const imageFile = getImageFileFromClipboard(e);
        if (!imageFile) return;
        e.preventDefault();
        if (!imageFile.type.startsWith('image/')) return;
        setContactUploading(true);
        setContactAttachmentError('');
        try {
            const res = await uploadContactAttachment(imageFile);
            if (res.data?.url) {
                setContactAttachments((prev) => [...prev, res.data.url]);
            }
        } catch (err) {
            console.error(err);
            setContactAttachmentError('添付のアップロードに失敗しました。');
        } finally {
            setContactUploading(false);
        }
    };

    const removeContactAttachment = (url: string) => {
        setContactAttachments((prev) => prev.filter((item) => item !== url));
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0c0a09] font-serif relative overflow-hidden">
            {/* Background Atmosphere */}
            <div className="absolute inset-0 z-0 opacity-40 bg-[url('https://images.unsplash.com/photo-1506459225024-1428097a7e18?q=80&w=2574&auto=format&fit=crop')] bg-cover bg-center mix-blend-overlay"></div>
            <div className="absolute inset-0 z-0 bg-gradient-to-t from-black via-black/80 to-transparent"></div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1 }}
                className="w-full max-w-md p-1 relative z-10"
            >
                {/* Main Card */}
                <div className="bg-[#1c1917] border border-[#d4af37] shadow-[0_0_50px_rgba(212,175,55,0.1)] rounded-sm p-8 text-[#d4af37]">

                    {/* Header */}
                    <div className="text-center mb-10">
                        <motion.div
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                        >
                            <h1 className="text-4xl font-bold tracking-widest font-serif mb-2 text-[#fcd34d]">Maison Portal</h1>
                            <p className="text-sm tracking-[0.3em] text-[#d4af37]/70 uppercase">The Sanctuary</p>
                            <p className="text-sm font-serif mt-4 tracking-widest text-[#d4af37]/80">笑顔の"独裁"マンションへ、ようこそ</p>
                        </motion.div>
                        <div className="h-px w-24 bg-[#d4af37] mx-auto mt-6 opacity-50"></div>
                    </div>

                    <form className="space-y-6" onSubmit={handleLogin}>
                        <div className="space-y-4">
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.4 }}
                            >
                                <label className="block text-xs uppercase tracking-widest mb-1 opacity-70">部屋番号（UserName or Email）</label>
                                <div className="relative">
                                    <Input
                                        type="text"
                                        className="bg-[#0c0a09] border-[#d4af37]/30 text-[#fcd34d] placeholder:text-[#d4af37]/20 h-12 pl-4 focus:ring-[#d4af37] focus:border-[#d4af37]"
                                        placeholder="居住者名またはメールアドレス"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        required
                                    />
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ x: 20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.5 }}
                            >
                                <label className="block text-xs uppercase tracking-widest mb-1 opacity-70">ルームキー(PassWord)</label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        className="bg-[#0c0a09] border-[#d4af37]/30 text-[#fcd34d] placeholder:text-[#d4af37]/20 h-12 pl-4 focus:ring-[#d4af37] focus:border-[#d4af37] pr-10"
                                        placeholder="パスコード"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#d4af37]/50 hover:text-[#d4af37]"
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </motion.div>
                        </div>

                        {error && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-red-500 text-sm text-center border border-red-900/50 bg-red-950/20 p-2"
                            >
                                {error}
                            </motion.div>
                        )}

                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-[#d4af37] hover:bg-[#ffe58f] text-black font-serif font-bold h-12 uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 relative group overflow-hidden"
                        >
                            {isLoading ? (
                                <span className="animate-pulse">Opening Gates...</span>
                            ) : (
                                <>
                                    <KeyRound size={18} />
                                    <span>入館する</span>
                                </>
                            )}
                            <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 w-full h-full skew-x-12"></div>
                        </motion.button>

                        {/* パスワードを忘れた人の入口（2026-10 デバッグ報告 TEST-013・TEST-026） */}
                        <div className="space-y-2" data-testid="login-forgot-password">
                            <button
                                type="button"
                                onClick={() => navigate('/forgot-password')}
                                className="w-full border border-[#d4af37]/40 text-[#fcd34d] hover:bg-[#d4af37]/10 h-11 text-sm tracking-wider font-serif flex items-center justify-center gap-2"
                            >
                                <HelpCircle size={16} />
                                パスワードを忘れた場合
                            </button>
                            <p className="text-[11px] text-center text-[#d4af37]/55 leading-relaxed">
                                登録したメールアドレスに、パスワードを決め直すリンクを送ります。<br />
                                メールが使えない場合は、
                                <button type="button" onClick={openPasswordContact} className="underline hover:text-[#fcd34d]">
                                    管理人に問い合わせ
                                </button>
                                てください。
                            </p>
                        </div>

                        <div className="flex justify-between items-center text-xs text-[#d4af37]/60 mt-4">
                            <button type="button" onClick={() => setShowContact(true)} className="hover:text-[#fcd34d] underline flex items-center gap-1">
                                <MessageSquare size={12} />
                                管理人に問い合わせる
                            </button>
                            <span className="opacity-50">or</span>
                        </div>

                        <div className="text-center mt-2">
                            <Button
                                variant="outline"
                                type="button"
                                onClick={() => navigate('/register')}
                                className="w-full border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10 hover:text-[#fcd34d] h-12 uppercase tracking-widest font-serif"
                            >
                                <DoorOpen size={18} className="mr-2" />
                                新規居住登録手続き
                            </Button>
                        </div>
                        <a
                            href="/downloads/maison-portal-native.apk"
                            download
                            className="w-full border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37]/10 hover:text-[#fcd34d] h-11 uppercase tracking-widest font-serif flex items-center justify-center gap-2 text-sm"
                        >
                            <Download size={17} />
                            Android版をダウンロード
                        </a>
                        <a
                            href="/downloads/maison-portal-native.ipa"
                            download
                            className="w-full border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37]/10 hover:text-[#fcd34d] h-11 uppercase tracking-widest font-serif flex items-center justify-center gap-2 text-sm"
                        >
                            <Download size={17} />
                            iPhone／iPad版をダウンロード
                        </a>
                    </form>
                </div>
            </motion.div>

            {/* Session Conflict Modal */}
            <AnimatePresence>
                {showSessionModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 10 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 10 }}
                            className="bg-[#1c1917] border border-[#d4af37] w-full max-w-md p-6 relative"
                        >
                            <button
                                onClick={() => {
                                    setShowSessionModal(false);
                                    setIsLoading(false);
                                }}
                                className="absolute top-4 right-4 text-[#d4af37]/50 hover:text-[#d4af37]"
                            >
                                <X size={24} />
                            </button>
                            <h2 className="text-xl font-bold text-[#fcd34d] mb-3">別のブラウザでログイン中</h2>
                            <p className="text-sm text-[#d4af37]/80 leading-relaxed mb-6">
                                {sessionConflictMessage ||
                                    'このアカウントは既に別のブラウザで利用中です。続行すると、そちらからは自動的にログアウトします。よろしいですか？'}
                            </p>
                            <div className="flex gap-3 justify-end">
                                <Button
                                    variant="outline"
                                    type="button"
                                    onClick={() => {
                                        setShowSessionModal(false);
                                        setIsLoading(false);
                                    }}
                                    className="border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10"
                                >
                                    キャンセル
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => performLogin(true)}
                                    className="bg-[#d4af37] text-black hover:bg-[#ffe58f]"
                                >
                                    続行してログイン
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showRestoreModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 10 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 10 }}
                            className="bg-[#1c1917] border border-[#d4af37] w-full max-w-md p-6 relative"
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    setShowRestoreModal(false);
                                    setIsLoading(false);
                                }}
                                className="absolute top-4 right-4 text-[#d4af37]/50 hover:text-[#d4af37]"
                            >
                                <X size={24} />
                            </button>
                            <h2 className="text-xl font-bold text-[#fcd34d] mb-3">アカウントを復元できます</h2>
                            <p className="text-sm text-[#d4af37]/80 leading-relaxed mb-3">{restoreMessage}</p>
                            {restoreUntil && (
                                <p className="text-xs text-[#d4af37]/60 mb-4">
                                    復元期限: {new Date(restoreUntil).toLocaleString('ja-JP')}
                                </p>
                            )}
                            <div className="rounded border border-[#d4af37]/30 bg-black/20 p-3 text-xs text-[#d4af37]/75 leading-relaxed mb-6">
                                元のメールアドレスは復元されません。復元後、画面の案内に従って新しいメールアドレスを登録してください。
                            </div>
                            <div className="flex gap-3 justify-end">
                                <Button
                                    variant="outline"
                                    type="button"
                                    onClick={() => {
                                        setShowRestoreModal(false);
                                        setIsLoading(false);
                                    }}
                                    className="border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10"
                                >
                                    今は復元しない
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => performLogin(false, true)}
                                    className="bg-[#d4af37] text-black hover:bg-[#ffe58f]"
                                >
                                    復元してログイン
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Contact Modal */}
            <AnimatePresence>
                {showContact && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto overscroll-contain"
                        data-testid="login-contact-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-label="管理人に問い合わせる"
                    >
                        {/* 添付を足して中身が画面より高くなっても、外枠をスクロールして下まで届くようにする（TEST-026） */}
                        <div className="min-h-full flex items-center justify-center p-4">
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            className="bg-[#1c1917] border border-[#d4af37] w-full max-w-lg p-6 relative my-4"
                        >
                            <button
                                onClick={closeContactModal}
                                className="absolute top-4 right-4 text-[#d4af37]/50 hover:text-[#d4af37]"
                            >
                                <X size={24} />
                            </button>

                            <h2 className="text-xl font-bold text-[#fcd34d] mb-4 flex items-center gap-2">
                                <MessageSquare /> Contact Administrator
                            </h2>
                            <p className="text-sm text-[#d4af37]/70 mb-6">
                                アカウント停止やログインに関するお問い合わせはこちらから。<br />
                                管理人およびマエストロが確認します。
                            </p>

                            {contactStatus === 'success' ? (
                                <div className="text-center py-8 text-green-400">
                                    <p className="text-lg font-bold">Message Sent!</p>
                                    <p className="text-sm">お問い合わせを受け付けました。</p>
                                </div>
                            ) : (
                                <form onSubmit={handleContactSubmit} className="space-y-4">
                                    <div>
                                        <label className="text-xs text-[#d4af37]/70 block mb-1">お名前</label>
                                        <Input
                                            required
                                            value={contactName}
                                            onChange={e => setContactName(e.target.value)}
                                            className="bg-black border-[#d4af37]/30 text-[#fcd34d]"
                                            placeholder="Your Name"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-[#d4af37]/70 block mb-1">連絡先メールアドレス</label>
                                        <Input
                                            type="email"
                                            required
                                            value={contactEmail}
                                            onChange={e => setContactEmail(e.target.value)}
                                            className="bg-black border-[#d4af37]/30 text-[#fcd34d]"
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-[#d4af37]/70 block mb-1">カテゴリ</label>
                                        <select
                                            value={contactCategory}
                                            onChange={e => setContactCategory(e.target.value)}
                                            className="w-full bg-black border border-[#d4af37]/30 rounded p-2 text-[#fcd34d] text-sm"
                                        >
                                            <option value={PASSWORD_RESET_CATEGORY}>パスワードを忘れた</option>
                                            <option value="Account Suspended">アカウント停止について</option>
                                            <option value="Login Issue">ログインできない</option>
                                            <option value="General">その他</option>
                                        </select>
                                        {contactCategory === PASSWORD_RESET_CATEGORY && (
                                            <p className="text-[11px] text-[#d4af37]/60 leading-relaxed mt-2" data-testid="login-contact-password-hint">
                                                登録したユーザー名（部屋番号）と、登録したメールアドレスを書いて送ってください。管理人が確認してご連絡します。
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-xs text-[#d4af37]/70 block mb-1">問い合わせ番号（任意・8桁）</label>
                                        <Input
                                            value={contactInquiryNumber}
                                            onChange={e => handleContactInquiryNumberChange(e.target.value)}
                                            inputMode="numeric"
                                            maxLength={8}
                                            pattern="[0-9]*"
                                            className="bg-black border-[#d4af37]/30 text-[#fcd34d]"
                                            placeholder="前の質問の続きの場合は、8桁の番号を同じ名前・同じメールと一緒に入力してください"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-[#d4af37]/70 block mb-1">メッセージ</label>
                                        <textarea
                                            required
                                            ref={contactMessageRef}
                                            value={contactMessage}
                                            onChange={e => setContactMessage(e.target.value)}
                                            onInput={(e) => autoResizeTextarea(e.currentTarget)}
                                            onPaste={handleContactPasteAttachment}
                                            className="w-full bg-black border border-[#d4af37]/30 rounded p-3 text-[#fcd34d] h-32 text-sm resize-none focus:outline-none focus:border-[#d4af37]"
                                            placeholder="お問い合わせ内容を入力してください..."
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-[#d4af37]/70 block mb-2">添付（任意・画像/動画）</label>
                                        <div className="flex items-center gap-3">
                                            <label
                                                htmlFor="login-contact-attachment"
                                                className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[#d4af37]/30 text-[#fcd34d] text-xs cursor-pointer"
                                            >
                                                <UploadCloud size={14} />
                                                追加する
                                            </label>
                                            <input
                                                id="login-contact-attachment"
                                                type="file"
                                                multiple
                                                accept="image/*,video/*"
                                                onChange={handleContactAttachmentChange}
                                                className="hidden"
                                            />
                                            {contactUploading && <span className="text-xs text-[#d4af37]/70">アップロード中...</span>}
                                        </div>
                                        {contactAttachmentError && <p className="text-red-500 text-xs mt-2">{contactAttachmentError}</p>}
                                        {contactAttachments.length > 0 && (
                                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {contactAttachments.map((url) => (
                                                    <div key={url} className="relative border border-[#d4af37]/30 rounded overflow-hidden">
                                                        {isVideoUrl(url) ? (
                                                            <video src={url} controls className="w-full max-h-32 bg-black" />
                                                        ) : (
                                                            <img src={url} alt="Attachment" className="w-full max-h-32 object-cover" />
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => removeContactAttachment(url)}
                                                            className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"
                                                        >
                                                            <X size={12} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {contactStatus === 'error' && <p className="text-red-500 text-xs">送信に失敗しました。</p>}

                                    {/* 送信ボタンは画面の下端に貼り付け、どの大きさの画面でも押せるようにする */}
                                    <div className="sticky bottom-0 -mx-6 -mb-6 px-6 pt-3 pb-6 bg-[#1c1917] border-t border-[#d4af37]/20">
                                        <Button
                                            type="submit"
                                            disabled={contactStatus === 'sending' || contactUploading}
                                            className="w-full bg-[#d4af37] text-black hover:bg-[#ffe58f] font-bold"
                                            data-testid="login-contact-submit"
                                        >
                                            {contactStatus === 'sending' ? 'Sending...' : 'Send Message'}
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
